import React from "react";
import { ReactFlow, getViewportForBounds } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useTranslation } from "react-i18next";
import AccountNode from "./AccountNode";
import DirectionEdge from "./DirectionEdge";
import { buttonClasses } from "../../common/tailwindClassnames";
import { getRouteStatus } from "../getRouteStatus";
import { FLOW_COLORS, FlowDirectionKey } from "./flowMapTypes";

const nodeTypes = { account: AccountNode };
const edgeTypes = { direction: DirectionEdge };

type RouteFlowMapProps = {
    nodes: any[];
    edges: any[];
    destinationCount: number;
    routeStatus?: string;
    onSelectAccount: (integrationId: string) => void;
    onSelectFlow: (destinationId: string, direction: FlowDirectionKey) => void;
    onAddDestination: () => void;
    // Mirrors the header's Route Enabled switch — a disabled Route moves
    // no data, so the whole graph is muted and the state is called out in words.
    routeEnabled?: boolean;
    // Pulses the "+ Add destination" button — set right after creating a new
    // Route so the user's next step is obvious.
    highlightAddDestination?: boolean;
    // Re-enables a disabled Route from the banner, the only place that is
    // reachable when the map has no outgoing arrow to carry the toggle.
    onEnableRoute?: () => void;
};

// Same framing fitView would aim for: 15% padding, never zoomed past natural size.
const FIT_PADDING = 0.15, MIN_ZOOM = 0.1, MAX_ZOOM = 1;

const LEGEND = [
    { label: "Enabled", className: "border-t-2 border-route-green w-6" },
    { label: "Disabled", className: "border-t-2 border-dashed border-gray-lines w-6" },
    { label: "Not available", className: "border-t-2 border-dashed border-gray-lines w-6" },
    // Same amber "!" badge as the node/arrow indicators.
    { label: "Needs setup", className: "w-4 h-4 rounded-full bg-orange-50 text-orange border border-orange-200 text-[10px] font-bold flex items-center justify-center", glyph: "!" },
];

const RouteFlowMap = ({ nodes, edges, destinationCount, routeStatus, onSelectAccount, onSelectFlow, onAddDestination, highlightAddDestination, onEnableRoute, routeEnabled = true }: RouteFlowMapProps) => {
    const { t } = useTranslation();
    const mapHeight = Math.max(280, destinationCount * 132 + 80);
    // The status pill describes data flow, so it only means anything while the
    // Route is enabled — otherwise the disabled chip says it all.
    const status = routeStatus && routeEnabled ? getRouteStatus(routeStatus, true, t) : undefined;

    // The pulse stops on its own or as soon as the user clicks the button.
    const [highlight, setHighlight] = React.useState(!!highlightAddDestination);
    React.useEffect(() => {
        if (!highlightAddDestination) return;
        setHighlight(true);
        const timer = setTimeout(() => setHighlight(false), 8000);
        return () => clearTimeout(timer);
    }, [highlightAddDestination]);

    const [rfInstance, setRfInstance] = React.useState<any>(null);
    const canvasRef = React.useRef<HTMLDivElement | null>(null);
    const nodeKey = nodes.map((n) => n.id).join(",");

    // The viewport is computed here, never by React Flow's fitView. fitView only
    // counts nodes that its ResizeObserver has MEASURED (getFitViewNodes filters on
    // n.measured.width) — a node's declared width/height makes it render but never
    // reaches the fit bounds. So any fit racing a measurement frames just the
    // already-measured subset: adding a destination framed the new node alone with
    // the provider shoved off-canvas, and removing the last one framed nothing.
    // Every node's position and size is declared in buildGraph, so the true bounds
    // are known synchronously — setViewport with our own math has nothing to race.
    // It also only sets a CSS transform, which cannot re-trigger the resize
    // observer the way fitView's layout nudges did.
    const frameView = React.useCallback(() => {
        const el = canvasRef.current;
        if (!rfInstance || !el || nodes.length === 0) return;
        const width = el.clientWidth, height = el.clientHeight;
        if (!width || !height) return;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const n of nodes) {
            minX = Math.min(minX, n.position.x);
            minY = Math.min(minY, n.position.y);
            maxX = Math.max(maxX, n.position.x + (n.width ?? 0));
            maxY = Math.max(maxY, n.position.y + (n.height ?? 0));
        }
        rfInstance.setViewport(getViewportForBounds(
            { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
            width, height, MIN_ZOOM, MAX_ZOOM, FIT_PADDING));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rfInstance, nodeKey]);

    // Reframe when the graph or the canvas height changes, and whenever the canvas
    // is resized (side drawer opening/closing, window resize). A zero-size canvas
    // is skipped — on a client-side navigation layout may not have happened yet —
    // and the observer delivers the real size as soon as it exists. The debounce
    // collapses a size that bounces during a drawer animation into one frame.
    React.useEffect(() => {
        const raf = requestAnimationFrame(frameView);
        const el = canvasRef.current;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const observer = typeof ResizeObserver === "undefined" ? undefined
            : new ResizeObserver(() => {
                clearTimeout(timer);
                timer = setTimeout(frameView, 150);
            });
        if (el) observer?.observe(el);
        return () => { cancelAnimationFrame(raf); clearTimeout(timer); observer?.disconnect(); };
    }, [frameView, mapHeight]);
    // Arrow clicks come from the edge's own label/hit-area handler, so the
    // callback rides along in edge data. onSelectFlow is an inline arrow that
    // closes over the panel's dirty state, so it must not be captured in the
    // memo — a stale copy would skip the "discard unsaved changes?" prompt.
    // A ref keeps the handler current without rebuilding the edges every render.
    const onSelectFlowRef = React.useRef(onSelectFlow);
    onSelectFlowRef.current = onSelectFlow;
    const selectFlow = React.useCallback(
        (destinationId: string, direction: FlowDirectionKey) => onSelectFlowRef.current(destinationId, direction), []);
    const edgesWithHandler = React.useMemo(
        () => edges.map((e) => ({ ...e, data: { ...e.data, onSelect: selectFlow } })),
        [edges, selectFlow]);

    return (
        <div className="border border-gray-lines rounded-lg bg-white mb-6">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-lines">
                <span className="text-lg font-bold text-off-black">{t("Flow map")}</span>
                {status && <span className={status.style}>{status.text}</span>}
                {!routeEnabled && (
                    <span className="rounded-full bg-neutral-gray-50 text-secondary-text border border-gray-lines px-2 py-0.5 text-xs font-semibold">
                        {t("Route Disabled")}
                    </span>
                )}
                <button
                    className={`${buttonClasses.lightGreen} ml-auto whitespace-nowrap ${
                        highlight ? "animate-pulse ring-2 ring-route-green ring-offset-2" : ""}`}
                    onClick={() => { setHighlight(false); onAddDestination(); }}>
                    {t("Add destination")}
                </button>
            </div>
            {/* One amber strip for whatever is keeping data from flowing. */}
            {!routeEnabled ? (
                <div className="px-5 py-2 bg-orange-50 border-b border-orange-200 text-sm text-off-black flex flex-wrap items-center gap-3">
                    <span>{t("This Route is disabled, so no data is moving along these flows.")}</span>
                    {/* The per-flow toggle lives on an outgoing arrow, and a Route
                        with no destinations has none — without this the state is a
                        dead end. */}
                    {onEnableRoute && (
                        <button type="button" className={`${buttonClasses.lightGreen} ml-auto whitespace-nowrap`}
                            onClick={() => onEnableRoute()}>
                            {t("Enable")}
                        </button>
                    )}
                </div>
            ) : routeStatus === "unhealthy" ? (
                <div className="px-5 py-2 bg-orange-50 border-b border-orange-200 text-sm text-off-black">
                    {t("This Route is unhealthy. Check the Logs tab for details.")}
                </div>
            ) : null}
            <div className={`overflow-x-auto ${routeEnabled ? "" : "grayscale opacity-60"}`}>
                <div ref={canvasRef} style={{ height: mapHeight, minWidth: 880 }}>
                    {/* arrowheads, one per state (SVG markers can't be Tailwind-coloured) */}
                    <svg style={{ height: 0, width: 0, position: "absolute" }}>
                        <defs>
                            {Object.entries(FLOW_COLORS).map(([state, color]) => (
                                <marker key={state} id={`flow-arrow-${state}`} markerWidth="10" markerHeight="10"
                                    refX="8" refY="4" orient="auto" markerUnits="strokeWidth">
                                    <path d="M0,0 L8,4 L0,8 z" fill={color} />
                                </marker>
                            ))}
                        </defs>
                    </svg>
                    <ReactFlow
                        nodes={nodes}
                        edges={edgesWithHandler}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        edgesFocusable={false}
                        panOnDrag={false}
                        zoomOnScroll={false}
                        zoomOnPinch={false}
                        zoomOnDoubleClick={false}
                        preventScrolling={false}
                        proOptions={{ hideAttribution: true }}
                        minZoom={MIN_ZOOM}
                        onInit={setRfInstance}
                        onNodeClick={(_: any, n: any) => onSelectAccount(n.id)}
                    />
                </div>
            </div>
            <div className="flex flex-wrap gap-5 px-5 py-2 border-t border-gray-lines text-xs text-secondary-text">
                {LEGEND.map((l: any) => (
                    <span key={l.label} className="flex items-center gap-2">
                        <span className={l.className}>{l.glyph}</span>{t(l.label as any)}
                    </span>
                ))}
            </div>
        </div>
    );
};

export default RouteFlowMap;
